import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Alarm, AlarmSeverity } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { Age, ErrorBox, Spinner } from '../components.js';
import { useNow, useResource } from '../hooks.js';
import {
  alarmState,
  ALARMS_REFRESH_MS,
  detailLines,
  notifyAlarmsChanged,
  type AlarmState,
} from '../lib/alarms.js';
import { formatTimestamp, PLACEHOLDER } from '../lib/format.js';

const SEVERITY_LABEL: Record<AlarmSeverity, string> = {
  critical: 'Critical',
  warning: 'Warning',
};
const STATE_LABEL: Record<AlarmState, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  cleared: 'Cleared',
};

export function SeverityBadge({ severity }: { severity: AlarmSeverity }) {
  return (
    <span className={`badge badge-sev-${severity}`}>
      <span className="dot" aria-hidden="true" />
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

function AlarmRow({
  a,
  now,
  busy,
  onAck,
}: {
  a: Alarm;
  now: number;
  busy: boolean;
  /** Absent for viewers (ADR 0010: acknowledging needs the operator role). */
  onAck: ((a: Alarm) => void) | undefined;
}) {
  const state = alarmState(a);
  return (
    <tr className={state === 'cleared' ? 'alarm-cleared' : undefined}>
      <td>
        <SeverityBadge severity={a.severity} />
      </td>
      <td>
        {a.site ? (
          <Link to={`/sites/${encodeURIComponent(a.site.id)}`}>{a.site.name ?? a.site.id}</Link>
        ) : (
          PLACEHOLDER
        )}
        {a.edge && <div className="mono small">{a.edge.edge_node_id}</div>}
      </td>
      <td>
        <Age ts={a.raised_at} now={now} />
        <div className="muted small">{formatTimestamp(a.raised_at)}</div>
      </td>
      <td>
        <div className="strong">{a.message}</div>
        <div className="muted small">
          {a.name}
          {detailLines(a.details).map((l) => (
            <span key={l}> · {l}</span>
          ))}
        </div>
      </td>
      <td>
        <span className={`badge badge-alarm-${state}`}>{STATE_LABEL[state]}</span>
        {a.cleared_at !== null && (
          <div className="muted small" title={formatTimestamp(a.cleared_at)}>
            cleared <Age ts={a.cleared_at} now={now} />
          </div>
        )}
        {a.acked_at !== null && (
          <div className="muted small" title={formatTimestamp(a.acked_at)}>
            ack by {a.acked_by?.email ?? 'unknown'}
          </div>
        )}
      </td>
      <td>
        {state === 'open' && onAck && (
          <button
            type="button"
            className="btn btn-small"
            disabled={busy}
            onClick={() => {
              onAck(a);
            }}
          >
            Ack
          </button>
        )}
      </td>
    </tr>
  );
}

/** Open alarms first (critical first), then the history; acknowledge an open alarm. */
export function AlarmsPage() {
  const api = useApi();
  const now = useNow(5000);
  const [params, setParams] = useSearchParams();
  const showAll = params.get('state') === 'all';
  const siteId = params.get('site_id') ?? '';
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const sites = useResource('sites', () => api.sites());
  const alarms = useResource(
    `alarms:${showAll ? 'all' : 'open'}:${siteId}`,
    (signal) =>
      api.alarms(
        siteId
          ? { state: showAll ? 'all' : 'open', site_id: siteId }
          : { state: showAll ? 'all' : 'open' },
        signal,
      ),
    ALARMS_REFRESH_MS,
  );

  function update(next: Record<string, string>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  }

  const canAck = useAuth().user?.permissions.operate ?? false;

  async function ack(a: Alarm) {
    setBusy(a.id);
    setActionError(null);
    try {
      await api.ackAlarm(a.id);
      alarms.reload();
      notifyAlarmsChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(null);
    }
  }

  const data = alarms.data;
  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Alarms</h1>
          {data && (
            <div className="muted small">
              {data.open} open · {data.open_critical} critical · {data.open_unacked} not
              acknowledged
            </div>
          )}
        </div>
      </div>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Alarms">
          {(['open', 'all'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={(s === 'all') === showAll ? 'active' : undefined}
              aria-pressed={(s === 'all') === showAll}
              onClick={() => {
                update({ state: s === 'all' ? 'all' : '' });
              }}
            >
              {s === 'all' ? 'All' : 'Open'}
            </button>
          ))}
        </div>
        <select
          aria-label="Site"
          value={siteId}
          onChange={(e) => {
            update({ site_id: e.target.value });
          }}
        >
          <option value="">All sites</option>
          {sites.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <span className="muted small">
          {alarms.loading && data ? 'Refreshing…' : `Refreshes every ${ALARMS_REFRESH_MS / 1000} s`}
        </span>
      </div>
      {alarms.error && <ErrorBox error={alarms.error} onRetry={alarms.reload} />}
      {actionError && <ErrorBox error={actionError} />}
      {!data && alarms.loading && <Spinner />}
      {data?.items.length === 0 && (
        <p className="empty">{showAll ? 'No alarms yet.' : 'No open alarms.'}</p>
      )}
      {data && data.items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Site / edge</th>
                <th>Since</th>
                <th>Alarm</th>
                <th>State</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <AlarmRow
                  key={a.id}
                  a={a}
                  now={now}
                  busy={busy === a.id}
                  onAck={canAck ? (x) => void ack(x) : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
